import LegalLayout from './LegalLayout';
import { PrivacyES } from './LegalSpanish';
import { useLanguage } from '../../i18n/LanguageContext';

const Privacy = ({ user, setUser }) => {
  const { lang } = useLanguage();
  return (
    <LegalLayout
      user={user}
      setUser={setUser}
      titleKey="footer.linkPrivacy"
      titleFallback="Privacy Policy"
      esTitle="Política de Privacidad"
      subtitleFallback="What we collect, why we collect it, and the rights you have over your data."
      esSubtitle="Qué recolectamos, por qué lo recolectamos y los derechos que tienes sobre tus datos."
      lastUpdated="February 17, 2026"
      testId="privacy-policy-page"
    >
      {lang === 'es' ? <PrivacyES /> : (
        <>
    <p>
      Match My Cleaner ("we", "us", "our") operates a marketplace connecting customers with independent cleaning professionals
      ("cleaners"). This Privacy Policy explains how we collect, use, share, and protect your personal information when you
      use our website, mobile experience, and related services (collectively, the "Service").
    </p>

    <h2>1. Information we collect</h2>

    <h3>1.1 Information you provide</h3>
    <ul>
      <li><strong>Account data</strong> — name, email, phone number, password (hashed), role (customer or cleaner).</li>
      <li><strong>Customer data</strong> — service address, zip code, booking preferences, payment method (tokenized via Stripe).</li>
      <li><strong>Cleaner data</strong> — professional bio, years of experience, certifications, services offered, hourly rate, availability schedule, service area, and the PII required to run a background check (collected directly by Checkr on its hosted form, not by us).</li>
      <li><strong>Communications</strong> — messages you send via Contact Us, in-app notifications, and disputes.</li>
    </ul>

    <h3>1.2 Information collected automatically</h3>
    <ul>
      <li><strong>Device & log data</strong> — IP address, browser type, pages viewed, timestamps.</li>
      <li><strong>Location</strong> — cleaners may share live geolocation to be discoverable nearby; this is opt-in via your browser.</li>
      <li><strong>Cookies & local storage</strong> — to keep you logged in (JWT) and remember preferences.</li>
    </ul>

    <h3>1.3 Information from third parties</h3>
    <ul>
      <li><strong>Stripe</strong> — payment status and last-4 of cards (we never see full card numbers).</li>
      <li><strong>Checkr</strong> — background check results (Clear, Consider, Suspended). We do not see or store the underlying SSN, DOB, or report details.</li>
      <li><strong>Map providers</strong> — OpenStreetMap tiles for displaying cleaner locations.</li>
    </ul>

    <h2>2. How we use your information</h2>
    <ul>
      <li>To create and manage your account, authenticate sessions, and personalize your experience.</li>
      <li>To match customers with cleaners by location, availability, and price.</li>
      <li>To process payments, calculate platform fees, and disburse cleaner earnings.</li>
      <li>To run and display background checks for trust and safety.</li>
      <li>To send transactional notifications (booking confirmations, payment receipts, tip alerts).</li>
      <li>To investigate disputes, fraud, and policy violations.</li>
      <li>To improve the Service through analytics on aggregated, non-identifying data.</li>
    </ul>

    <h2>3. How we share your information</h2>
    <p>We do <strong>not</strong> sell your personal data. We share it only with:</p>
    <ul>
      <li><strong>The other party to your booking</strong> — customers see cleaner profiles; cleaners see customer name and service address only after a booking is confirmed.</li>
      <li><strong>Service providers</strong> — Stripe (payments), Checkr (background checks), MongoDB Atlas (hosting), email/SMS providers (transactional messages).</li>
      <li><strong>Law enforcement</strong> — when legally required, or to investigate safety incidents.</li>
      <li><strong>Successors</strong> — in the event of a merger, acquisition, or asset sale, your data may transfer to the new entity under equivalent privacy protections.</li>
    </ul>

    <h2>4. Data retention</h2>
    <p>
      We keep your account data for as long as your account is active. Booking, payment, and dispute records are retained
      for up to 7 years for tax, fraud, and regulatory compliance. You may request deletion of your account at any time
      via Contact Us — we'll honor the request within 30 days, except for records we're legally required to retain.
    </p>

    <h2>5. Your rights</h2>
    <p>Depending on where you live, you may have the right to:</p>
    <ul>
      <li>Access the personal data we hold about you.</li>
      <li>Correct inaccurate or incomplete data.</li>
      <li>Delete your data ("right to be forgotten").</li>
      <li>Object to or restrict certain processing.</li>
      <li>Receive your data in a portable format.</li>
      <li>Withdraw consent at any time (where processing is based on consent).</li>
    </ul>
    <p>To exercise any of these, email us via the Contact page.</p>

    <h2>6. Security</h2>
    <p>
      Passwords are hashed with bcrypt. Sessions use signed JWT tokens. Payments are tokenized via Stripe's PCI-DSS Level 1
      infrastructure. We use TLS in transit and encryption at rest for stored data. No system is perfectly secure — please
      use a strong, unique password and keep your account credentials private.
    </p>

    <h2>7. Children</h2>
    <p>
      The Service is not intended for anyone under 18. We do not knowingly collect data from minors. If you believe a minor
      has provided us data, contact us and we will delete it.
    </p>

    <h2>8. International transfers</h2>
    <p>
      Our infrastructure is primarily in the United States. By using the Service from outside the U.S., you consent to
      processing of your data in the U.S., where data protection laws may differ from your jurisdiction.
    </p>

    <h2>9. Changes to this policy</h2>
    <p>
      We may update this policy periodically. When we make material changes, we'll notify you via email and post a notice
      on the platform. Your continued use after the effective date means you accept the revised policy.
    </p>

    <h2>10. Contact us</h2>
    <p>
      Questions about this policy or how we handle your data? Reach us via the Contact page on the site.
    </p>
        </>
      )}
    </LegalLayout>
  );
};

export default Privacy;
